import { RefObject, useState } from 'react';
import { dispose, init } from 'echarts/core';
import type { BarSeriesOption, EChartsOption } from 'echarts';
import { WaterTooltipGraph } from '../components/GraphTooltip';
import ReactDOMServer from 'react-dom/server';
import { MonthWaterOverviewInfo, WaterOverviewInfo } from 'providers/types/api-private';
import { colors } from 'styles/colors';
import { generateSubTitleByData, generateXAxisNames } from '../helpers';
import { getUserProfile } from 'helpers/userProfile';
import { YAXisOption } from 'echarts/types/dist/shared';
import {
  defaultConfigsChart, defaultXAxisConfig, defaultYAxisConfig, formatterXAxisLabel,
  mediaQueryXAxis,
} from '~/pages/NewOverview/constants';
import { DISABLED_DATA_INFO } from '../constants';
import { formatNumberWithFractionDigits, thousandPointFormat } from '~/helpers/thousandFormatNumber';

interface UseGenerateGraphReturn {
  renderWaterChart: (data: WaterOverviewInfo | undefined) => void;
}

interface TooltipParams {
  name: string;
  value: number;
}

export function useGenerateWaterGraph(
  containerRef: RefObject<HTMLDivElement>,
  disabled: boolean,
  expanded: boolean,
): UseGenerateGraphReturn {
  const [profile] = useState(getUserProfile);
  const unitMeasure = JSON.parse(profile.prefs ?? '{}')?.water ?? 'cubic';
  const measure = unitMeasure === 'liters' ? 'L' : 'm³';

  function convertByMeasure(value: number): number {
    return unitMeasure === 'liters' ? value : value / 1000;
  }

  function createChartOptions(optionsData: BarSeriesOption['data'], data: WaterOverviewInfo): EChartsOption {
    const option: EChartsOption = {
      title: createChartTitle(data),
      grid: {
        left: expanded ? '80' : '10',
        right: expanded ? '80' : '20',
        top: expanded ? '50' : '40',
        bottom: '0%',
        containLabel: true,
      },
      tooltip: {
        show: !disabled,
        trigger: 'axis',
        formatter: (value) => formatterTooltip(value, data.TYPE),
        axisPointer: {
          type: 'shadow',
        },
        backgroundColor: 'transparent',
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        borderWidth: 0,
      },
      xAxis: createXAxis(data),
      yAxis: createYAxis(expanded),
      series: createSeriesData(optionsData, expanded),
      media: [
        mediaQueryXAxis,
      ],
    };

    return option;
  }

  function formatterTooltip([value]: TooltipParams[], type: 'month' | 'day'): string {
    return ReactDOMServer.renderToString(
      <WaterTooltipGraph
        date={value.name}
        value={value.value}
        unit={measure}
        type={type}
      />,
    );
  }

  function createChartTitle(data: WaterOverviewInfo): EChartsOption['title'] {
    return {
      text: `${formatNumberWithFractionDigits(convertByMeasure(data.TOTAL), { minimum: 0, maximum: 2 })} ${measure}`,
      subtext: generateSubTitleByData(data),
      textAlign: 'center',
      left: '50%',
      top: '4',
      padding: 0,
      itemGap: 2,
      textStyle: {
        ...defaultConfigsChart,
        fontWeight: 700,
        fontSize: 20,
        color: colors.text['main-650'],
      },
      subtextStyle: {
        ...defaultConfigsChart,
        fontSize: 12,
        color: colors.text['main-650'],
      },
    };
  }

  function createXAxis(data: WaterOverviewInfo): EChartsOption['xAxis'] {
    return {
      ...defaultXAxisConfig,
      data: generateXAxisNames(data),
      axisLabel: {
        ...defaultXAxisConfig.axisLabel,
        formatter: (value) => formatterXAxisLabel(value, data.TYPE, disabled),
      },
    } as EChartsOption['xAxis'];
  }

  function createSeriesData(optionsData: BarSeriesOption['data'], expanded: boolean): EChartsOption['series'] {
    return [
      {
        type: 'bar',
        barWidth: expanded ? '38px' : '12px',
        data: optionsData,
        stack: 'group',
        barCategoryGap: expanded ? '0%' : '10%',
        itemStyle: {
          borderRadius: [0, 0, 0, 0],
        },
        ...(disabled ? {
          cursor: 'not-allowed',
          color: colors.icon['disabled-300'],
        } : {
          cursor: 'pointer',
          color: colors.brand.primary.base,
        }),
      },
    ];
  }

  function renderWaterChart(dataInfo: WaterOverviewInfo | undefined): (() => void) | void {
    if (!containerRef.current) return;

    let newData = dataInfo as MonthWaterOverviewInfo;
    if (!dataInfo) {
      newData = DISABLED_DATA_INFO;
    }

    const formattedData = newData.HISTORY.map((item) => convertByMeasure(item.USAGE));

    dispose(containerRef.current);
    const chartInstance = init(containerRef.current);

    const resizeHandler = () => {
      chartInstance.resize();
    };

    window.addEventListener('resize', resizeHandler);

    chartInstance.setOption(createChartOptions(formattedData, newData));

    return (): void => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.dispose();
    };
  }

  return {
    renderWaterChart,
  };
}

function createYAxis(expanded: boolean): EChartsOption['yAxis'] {
  return {
    ...defaultYAxisConfig,
    offset: expanded ? 60 : 8,
    axisLabel: {
      ...defaultYAxisConfig.axisLabel,
      formatter: (value) => thousandPointFormat(value),
    },
  } as YAXisOption;
}
